import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import {
  Search,
  BarChart3,
  Download,
  Users,
  ShieldCheck,
  Settings,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { cn } from '../../lib/utils';
import WorldGlobe from '../ui/WorldGlobe';

const ICON_URL = '/ciaolead-icon.png';

const navItems = [
  { 
    name: 'Search', 
    href: '/dashboard', 
    icon: Search,
    roles: ['admin', 'user']
  },
  { 
    name: 'Reports', 
    href: '/reports', 
    icon: BarChart3,
    roles: ['admin', 'user']
  },
  { 
    name: 'Export', 
    href: '/export', 
    icon: Download,
    roles: ['admin', 'user']
  },
  {
    name: 'Team',
    href: '/team',
    icon: Users,
    roles: ['admin']
  },
  {
    name: 'Admin',
    href: '/admin',
    icon: ShieldCheck,
    superAdminOnly: true,  // P6.6: visible only to the platform super-admin
  },
  {
    name: 'Settings',
    href: '/settings',
    icon: Settings,
    roles: ['admin', 'user']
  },
];

const Sidebar = ({ isOpen, onClose, collapsed = false, onToggleCollapse }) => {
  const location = useLocation();
  const { user, isSuperAdmin } = useAuth();

  const filteredNavItems = navItems.filter(item =>
    item.superAdminOnly
      ? isSuperAdmin
      : item.roles.includes(user?.role || 'user')
  );

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-40 sidebar-overlay"
          onClick={onClose}
          data-testid="sidebar-overlay"
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 z-50 h-screen border-r border-[var(--world-border)] flex flex-col overflow-hidden",
          "transform transition-all duration-200 ease-in-out lg:translate-x-0",
          isOpen ? "translate-x-0" : "-translate-x-full",
          collapsed ? "w-16" : "w-64"
        )}
        style={{ backgroundColor: 'var(--bg-sidebar)' }}
        data-testid="sidebar"
        data-collapsed={collapsed ? "true" : "false"}
      >
        {/* Header */}
        <div className={cn(
          "flex h-16 items-center border-b border-white/5",
          collapsed ? "flex-col justify-center gap-0.5 px-2" : "justify-between px-4"
        )}>
          {collapsed ? (
            <>
              <Link to="/dashboard" data-testid="sidebar-logo">
                <img
                  src={ICON_URL}
                  alt="CiaoLead"
                  className="h-10 w-10 object-contain"
                />
              </Link>
              <Button
                variant="ghost"
                size="icon"
                className="hidden lg:inline-flex h-5 w-5 text-slate-400 hover:text-white hover:bg-white/10"
                onClick={onToggleCollapse}
                aria-label="Expand sidebar"
                title="Expand sidebar"
                data-testid="sidebar-collapse-toggle"
              >
                <ChevronRight className="h-3 w-3" />
              </Button>
            </>
          ) : (
            <>
              <Link to="/dashboard" className="flex items-center gap-2.5" data-testid="sidebar-logo">
                <img
                  src={ICON_URL}
                  alt="CiaoLead"
                  className="h-12 w-12 object-contain"
                />
                <span className="font-display text-white font-semibold text-base tracking-wide">CiaoLead</span>
              </Link>
              <div className="flex items-center">
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden text-slate-300 hover:text-white hover:bg-white/10"
                  onClick={onClose}
                  data-testid="sidebar-close-button"
                >
                  <X className="h-5 w-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="hidden lg:inline-flex h-8 w-8 text-slate-300 hover:text-white hover:bg-white/10"
                  onClick={onToggleCollapse}
                  aria-label="Collapse sidebar"
                  title="Collapse sidebar"
                  data-testid="sidebar-collapse-toggle"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Navigation */}
        <nav className={cn(
          "flex-1 overflow-y-auto custom-scrollbar",
          collapsed ? "p-2" : "p-4"
        )}>
          <ul className="space-y-1">
            {filteredNavItems.map((item) => {
              const isActive = location.pathname === item.href;
              const Icon = item.icon;
              
              return (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    onClick={onClose}
                    title={collapsed ? item.name : undefined}
                    className={cn(
                      "sidebar-link flex items-center rounded-lg text-sm font-medium",
                      collapsed ? "justify-center px-2 py-2.5" : "gap-3 px-3 py-2.5",
                      isActive
                        ? "world-nav-active"
                        : "world-nav-inactive"
                    )}
                    data-testid={`sidebar-nav-${item.name.toLowerCase()}`}
                  >
                    <Icon className="h-5 w-5" />
                    {!collapsed && item.name}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Decorative faded globe (bottom of sidebar) */}
        {!collapsed && (
          <div className="relative h-24 overflow-hidden pointer-events-none" aria-hidden="true">
            <WorldGlobe
              size={190}
              className="opacity-40"
              style={{ left: '50%', bottom: '-95px', transform: 'translateX(-50%)' }}
            />
          </div>
        )}

        {/* Powered by credit */}
        {!collapsed && (
          <div className="px-4 pb-1 text-center">
            <span className="text-[10px] text-slate-400 leading-tight">
              Powered by <span className="whitespace-nowrap">Echo Maik Studio</span> // EMS
            </span>
          </div>
        )}

        {/* Footer - User info */}
        <div className={cn(
          "border-t border-white/5",
          collapsed ? "p-2" : "p-4"
        )}>
          <div
            className={cn(
              "flex items-center py-2",
              collapsed ? "justify-center px-0" : "gap-3 px-3"
            )}
            title={collapsed ? `${user?.email} (${user?.role})` : undefined}
          >
            <div
              className="h-8 w-8 rounded-full flex items-center justify-center shrink-0"
              style={{ backgroundImage: 'linear-gradient(135deg, #3b82f6, #6366f1)' }}
            >
              <span className="text-sm font-medium text-white">
                {user?.email?.[0]?.toUpperCase() || 'U'}
              </span>
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate" data-testid="sidebar-user-email">
                  {user?.email}
                </p>
                <p className="text-xs text-slate-400 capitalize" data-testid="sidebar-user-role">
                  {user?.role} · {user?.company_name}
                </p>
              </div>
            )}
          </div>

          {/* Dot pagination indicator */}
          {!collapsed && (
            <div className="world-dots pt-2" aria-hidden="true">
              <span className="dot" />
              <span className="dot" />
              <span className="dot bar" />
              <span className="dot" />
              <span className="dot" />
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
